import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { Conversation, ConversationContent, ConversationScrollButton } from "../../../ai-elements/conversation.js";
import { memo, useState, useRef, useMemo, useCallback, useEffect } from "react";
/* empty css                          */
import { useConversationScrollDetection } from "../../hooks/useConversationScrollDetection.js";
import ConciergeWelcome from "../ConciergeWelcome.js";
import { ConversationSkeleton } from "../ConversationSkeleton.js";
import { MessageListV2 } from "../MessageListV2.js";
import { ConciergeSidebar } from "../ConciergeSidebar.js";
import { ConciergeHomeMobileNav } from "../ConciergeHomeMobileNav.js";
import { useWidgetConfig, customColor, useResolveColor, ConciergePanelWidget, PANEL_CHAT_INPUT_PRESETS, CHAT_INPUT_PRESETS } from "@brandclub/bc-widgets";
import { useIsMobile } from "./useIsMobile.js";
import { ConciergeInputArea } from "./ConciergeInputArea.js";
import { useLiveTakeoverState } from "../../hooks/useLiveTakeoverState.js";
import { useConciergeSupportEvents } from "../../hooks/useConciergeSupportEvents.js";
import { SupportThemeProvider } from "../SupportThemeProvider.js";
import { endLiveAgent } from "../../api.js";
import { getHeadersWithUniqueVisitorId } from "../../../Auth/index.js";
const ConciergeChatAreaV2Component = ({
  appName,
  conciergeName,
  brandId,
  conversationId,
  isEmbedded = false,
  isOnChatWidget = false,
  apiBaseUrl,
  userFirstName,
  pageContext,
  conversationHistory,
  followUpQuestions,
  latestFollowUps,
  isLoadingFollowUps,
  streamingDevInfo,
  isActivelyStreaming,
  transientStatus,
  streamingAIResponse,
  onStop,
  isLoadingConversationQuery,
  isPendingAssistantResponse,
  isPollingTimedOut,
  onRetry,
  onRetryLastMessage,
  streamErrorMessage,
  onStreamErrorRetry,
  onLiveTakeoverActive,
  onTakeoverConversationChange,
  handleSearch,
  onUpdateFeedback,
  onVariantSelect,
  onTrackAccessPoint,
  onHomeClick,
  isSidebarExpanded = true,
  brandLogoUrl,
  searchInputValue,
  setSearchInputValue,
  mobileSidebarProps,
  isSharedView,
  sharedByUserName,
  sourceConversationId,
  sourceCheckpointIndex,
  onOpenShareDialog,
  onShareClick,
  viewMode,
  onSupportEventsReconnect
}) => {
  const pdpStacklineSku = (pageContext == null ? void 0 : pageContext.pageType) === "pdp" ? pageContext.stacklineSku : null;
  const isMobile = useIsMobile();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const showWelcome = conversationHistory.length === 0 && !isLoadingConversationQuery && !isPendingAssistantResponse && !conversationId;
  const hasImageCarousel = false;
  const lastMsg = conversationHistory[conversationHistory.length - 1];
  const hasActivePartStreaming = (lastMsg == null ? void 0 : lastMsg.role) === "assistant" && "parts" in lastMsg && Array.isArray(lastMsg.parts) && lastMsg.parts.length > 0;
  const showTypingIndicator = isActivelyStreaming && !streamingAIResponse && !hasActivePartStreaming;
  const liveSupportParts = useConciergeSupportEvents(conversationId, apiBaseUrl, onSupportEventsReconnect);
  const liveReplyAnchorsRef = useRef(/* @__PURE__ */ new Map());
  const messagesWithLiveReplies = useMemo(() => {
    var _a, _b;
    const persistedHumanReplyTimestamps = /* @__PURE__ */ new Set();
    for (const msg of conversationHistory) {
      const parts = msg.parts;
      if (!Array.isArray(parts)) continue;
      for (const p of parts) {
        if ((p == null ? void 0 : p.type) === "data-human-reply" && typeof ((_a = p == null ? void 0 : p.data) == null ? void 0 : _a.postedAt) === "string") {
          persistedHumanReplyTimestamps.add(p.data.postedAt);
        }
      }
    }
    const liveReplies = [];
    for (let i = 0; i < liveSupportParts.length; i++) {
      const part = liveSupportParts[i];
      if ((part == null ? void 0 : part.type) !== "data-human-reply") continue;
      const postedAt = (_b = part == null ? void 0 : part.data) == null ? void 0 : _b.postedAt;
      if (postedAt && persistedHumanReplyTimestamps.has(postedAt)) continue;
      const stableId = postedAt ?? `idx-${i}`;
      if (!liveReplyAnchorsRef.current.has(stableId)) {
        liveReplyAnchorsRef.current.set(stableId, conversationHistory.length);
      }
      const anchor = liveReplyAnchorsRef.current.get(stableId);
      liveReplies.push({
        anchor,
        order: i,
        message: {
          id: `live-human-reply-${stableId}`,
          role: "assistant",
          parts: [part]
        }
      });
    }
    liveReplies.sort((a, b) => a.anchor - b.anchor || a.order - b.order);
    if (liveReplies.length === 0) return conversationHistory;
    const result = [];
    let liveIdx = 0;
    for (let i = 0; i <= conversationHistory.length; i++) {
      while (liveIdx < liveReplies.length && liveReplies[liveIdx].anchor === i) {
        result.push(liveReplies[liveIdx].message);
        liveIdx++;
      }
      if (i < conversationHistory.length) {
        result.push(conversationHistory[i]);
      }
    }
    while (liveIdx < liveReplies.length) {
      result.push(liveReplies[liveIdx].message);
      liveIdx++;
    }
    return result;
  }, [conversationHistory, liveSupportParts]);
  const scrollDetectionParams = useMemo(
    () => ({
      followUpQuestions,
      isActivelyStreaming,
      conversationHistoryLength: conversationHistory.length,
      showWelcome,
      searchInputValue: ""
    }),
    [followUpQuestions, isActivelyStreaming, conversationHistory.length, showWelcome]
  );
  const { bottomRef } = useConversationScrollDetection(scrollDetectionParams);
  const stableHandleSearch = useMemo(() => handleSearch, [handleSearch]);
  const handleWelcomeSearch = useCallback(
    (searchTerm) => {
      onTrackAccessPoint == null ? void 0 : onTrackAccessPoint("quick_prompts", searchTerm);
      stableHandleSearch(searchTerm);
    },
    [stableHandleSearch, onTrackAccessPoint]
  );
  const handleFollowUpSearch = useCallback(
    (searchTerm) => {
      onTrackAccessPoint == null ? void 0 : onTrackAccessPoint("follow_up_question", searchTerm);
      stableHandleSearch(searchTerm);
    },
    [stableHandleSearch, onTrackAccessPoint]
  );
  const welcomeProps = useMemo(
    () => ({
      onSearch: handleWelcomeSearch,
      brandId: brandId ? parseInt(brandId) : void 0,
      isEmbedded,
      apiBaseUrl,
      userFirstName,
      pageContext,
      brandLogoUrl,
      brandName: appName,
      conciergeName,
      searchInputValue,
      setSearchInputValue,
      handleSearch,
      isActivelyStreaming,
      isMobile
    }),
    [
      handleWelcomeSearch,
      brandId,
      isEmbedded,
      apiBaseUrl,
      userFirstName,
      pageContext,
      brandLogoUrl,
      appName,
      conciergeName,
      searchInputValue,
      setSearchInputValue,
      handleSearch,
      isActivelyStreaming,
      isMobile
    ]
  );
  const SimpleWelcomeHeader = useMemo(
    () => /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:flex-col tw:items-center tw:justify-center tw:w-full tw:text-center", children: [
      /* @__PURE__ */ jsx("p", { className: "tw:font-bold tw:text-[16px] tw:leading-[32px] tw:tracking-[2px] tw:uppercase tw:text-primary", children: "CONCIERGE" }),
      /* @__PURE__ */ jsxs("h1", { className: "tw:font-semibold tw:text-[24px] tw:leading-[32px] tw:tracking-[0.08px] tw:mt-4 tw:text-primary", children: [
        "Let us help you find what you've been looking for",
        userFirstName ? `, ${userFirstName}` : "",
        "."
      ] })
    ] }),
    [userFirstName]
  );
  const latestUserDelivery = useMemo(() => {
    let latest;
    for (const part of liveSupportParts) {
      if (part.type === "data-user-message-mirrored") latest = "delivered";
      else if (part.type === "data-user-message-delivery-failed") latest = "failed";
    }
    return latest;
  }, [liveSupportParts]);
  const allParts = useMemo(() => {
    const acc = [];
    for (const msg of conversationHistory) {
      if (msg && "parts" in msg && Array.isArray(msg.parts)) {
        acc.push(...msg.parts);
      }
    }
    acc.push(...liveSupportParts);
    return acc;
  }, [conversationHistory, liveSupportParts]);
  const takeover = useLiveTakeoverState(allParts);
  const [endingLocally, setEndingLocally] = useState(false);
  useEffect(() => {
    setEndingLocally(false);
  }, [conversationId]);
  const lastSeenStartedAtRef = useRef(takeover.startedAt);
  useEffect(() => {
    if (takeover.startedAt && takeover.startedAt !== lastSeenStartedAtRef.current) {
      lastSeenStartedAtRef.current = takeover.startedAt;
      setEndingLocally(false);
    }
  }, [takeover.startedAt]);
  const effectiveTakeoverActive = takeover.active && !endingLocally;
  useEffect(() => {
    if (effectiveTakeoverActive && onLiveTakeoverActive) {
      onLiveTakeoverActive();
    }
  }, [effectiveTakeoverActive, onLiveTakeoverActive]);
  useEffect(() => {
    if (!onTakeoverConversationChange) return;
    onTakeoverConversationChange(effectiveTakeoverActive && conversationId ? conversationId : null);
  }, [effectiveTakeoverActive, conversationId, onTakeoverConversationChange]);
  const conversationContent = useMemo(() => {
    if (isLoadingConversationQuery && conversationHistory.length === 0) {
      return /* @__PURE__ */ jsx(ConversationSkeleton, {});
    }
    if (showWelcome) {
      return /* @__PURE__ */ jsx(ConciergeWelcome, { ...welcomeProps });
    }
    return /* @__PURE__ */ jsx(
      MessageListV2,
      {
        conversationId,
        messages: messagesWithLiveReplies,
        showTypingIndicator: showTypingIndicator && !effectiveTakeoverActive,
        streamingAIResponse,
        isActivelyStreaming,
        transientStatus,
        streamingDevInfo,
        followUpQuestions,
        latestFollowUps,
        isLoadingFollowUps,
        onSearch: handleFollowUpSearch,
        onUpdateFeedback,
        onVariantSelect,
        bottomRef,
        apiBaseUrl,
        isPendingAssistantResponse,
        isPollingTimedOut,
        onRetry,
        onRetryLastMessage,
        streamErrorMessage,
        onStreamErrorRetry,
        liveTakeoverActive: effectiveTakeoverActive,
        latestUserDelivery,
        isSharedView,
        sharedByUserName,
        sourceConversationId,
        sourceCheckpointIndex,
        pdpStacklineSku,
        isOnChatWidget,
        brandLogoUrl,
        onOpenShareDialog,
        viewMode
      }
    );
  }, [
    isLoadingConversationQuery,
    showWelcome,
    hasImageCarousel,
    SimpleWelcomeHeader,
    welcomeProps,
    conversationId,
    conversationHistory,
    messagesWithLiveReplies,
    showTypingIndicator,
    streamingAIResponse,
    isActivelyStreaming,
    transientStatus,
    streamingDevInfo,
    followUpQuestions,
    latestFollowUps,
    isLoadingFollowUps,
    handleFollowUpSearch,
    onUpdateFeedback,
    onVariantSelect,
    bottomRef,
    isPendingAssistantResponse,
    isPollingTimedOut,
    onRetry,
    onRetryLastMessage,
    streamErrorMessage,
    onStreamErrorRetry,
    effectiveTakeoverActive,
    latestUserDelivery,
    isSharedView,
    sharedByUserName,
    sourceConversationId,
    sourceCheckpointIndex,
    pdpStacklineSku,
    isOnChatWidget,
    brandLogoUrl,
    onOpenShareDialog
  ]);
  const directPanelState = useWidgetConfig("concierge-panel", {
    preset: "vertical-expanded",
    mainBgColor: customColor("transparent"),
    pillLayout: "vertical",
    pillShowIcon: false,
    pillBorderRadius: 100,
    pillBorderColor: customColor("#F3F3F3"),
    pillBorderWidth: 1,
    pillShadowEnabled: true,
    pillBgColor: customColor("#ffffff"),
    pillTextColor: customColor("#181d56"),
    pillShadow: "0px 4px 16px 0 rgba(0, 0, 0, 0.05)",
    pillContainerBgColor: customColor("transparent"),
    pillContainerBorderRadius: 12,
    inputBorderColor: customColor("#DCDDDC"),
    inputBorderWidth: 1,
    inputBorderRadius: 20,
    inputMinHeight: 116,
    inputShadowEnabled: false,
    inputShadow: "0px 4px 16px 0 rgba(0, 0, 0, 0.05)"
  });
  const directResolveColor = useResolveColor();
  const handleEndLiveAgent = useCallback(async () => {
    if (!conversationId) return;
    setEndingLocally(true);
    try {
      const headers = await getHeadersWithUniqueVisitorId();
      await endLiveAgent({ conversationId, headersToInject: headers, apiBaseUrl });
    } catch (err) {
      console.warn("Failed to end live agent", err);
      setEndingLocally(false);
    }
  }, [conversationId, apiBaseUrl]);
  const renderContent = (panelState, resolveColor) => {
    const bgColor = isOnChatWidget ? resolveColor(panelState.mainBgColor) : void 0;
    return /* @__PURE__ */ jsx(SupportThemeProvider, { children: /* @__PURE__ */ jsxs(
      "div",
      {
        className: "chat-container tw:relative tw:flex tw:flex-1 tw:flex-col",
        style: {
          minHeight: 0,
          maxHeight: "100%",
          overflow: "hidden",
          ...bgColor && bgColor !== "transparent" ? { backgroundColor: bgColor } : {}
        },
        children: [
          viewMode !== "read-only" && isMobile && mobileSidebarProps && /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                position: "absolute",
                inset: 0,
                zIndex: 50,
                display: "flex",
                pointerEvents: isMobileSidebarOpen ? "auto" : "none"
              },
              children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    style: {
                      position: "absolute",
                      inset: 0,
                      backgroundColor: "rgba(0,0,0,0.3)",
                      opacity: isMobileSidebarOpen ? 1 : 0,
                      transition: "opacity 0.25s ease-in-out"
                    },
                    onClick: () => setIsMobileSidebarOpen(false)
                  }
                ),
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    style: {
                      position: "relative",
                      zIndex: 1,
                      height: "100%",
                      transform: isMobileSidebarOpen ? "translateX(0)" : "translateX(-100%)",
                      transition: "transform 0.25s ease-in-out"
                    },
                    children: /* @__PURE__ */ jsx(
                      ConciergeSidebar,
                      {
                        ...mobileSidebarProps,
                        onConversationSelect: (id, mode) => {
                          var _a;
                          (_a = mobileSidebarProps.onConversationSelect) == null ? void 0 : _a.call(mobileSidebarProps, id, mode);
                          setIsMobileSidebarOpen(false);
                        },
                        onComposeClick: () => {
                          var _a;
                          (_a = mobileSidebarProps.onComposeClick) == null ? void 0 : _a.call(mobileSidebarProps);
                          setIsMobileSidebarOpen(false);
                        },
                        onChatsClick: () => {
                          var _a;
                          (_a = mobileSidebarProps.onChatsClick) == null ? void 0 : _a.call(mobileSidebarProps);
                          setIsMobileSidebarOpen(false);
                        },
                        isExpanded: true,
                        onToggle: () => setIsMobileSidebarOpen(false),
                        mobileOverlay: true
                      }
                    )
                  }
                )
              ]
            }
          ),
          viewMode !== "read-only" && isMobile && mobileSidebarProps && /* @__PURE__ */ jsx(
            ConciergeHomeMobileNav,
            {
              brandLogoUrl,
              onMenuClick: () => setIsMobileSidebarOpen(true),
              onCloseClick: onHomeClick,
              onShareClick
            }
          ),
          isEmbedded && showWelcome ? /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(
              "div",
              {
                className: "concierge-conversation-scroll",
                style: {
                  flex: 1,
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  maxHeight: "100%"
                },
                children: /* @__PURE__ */ jsx("div", { style: { flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }, children: conversationContent })
              }
            ),
            viewMode !== "read-only" && /* @__PURE__ */ jsx(
              ConciergeInputArea,
              {
                searchInputValue,
                setSearchInputValue,
                handleSearch,
                isActivelyStreaming,
                conversationHistoryLength: conversationHistory.length,
                onTrackAccessPoint,
                onStop,
                widgetId: "concierge-panel-chat-input",
                displayName: "Chat Input (Panel Home)",
                presets: PANEL_CHAT_INPUT_PRESETS,
                forceMobile: true,
                initialState: { preset: "expanded", ...PANEL_CHAT_INPUT_PRESETS.expanded },
                liveTakeoverActive: effectiveTakeoverActive,
                adminName: takeover.adminName,
                adminAvatarUrl: takeover.adminAvatarUrl,
                onEndLiveAgent: handleEndLiveAgent
              }
            )
          ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(
              "div",
              {
                className: "non-embedded-container concierge-conversation-scroll",
                style: {
                  flex: 1,
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  maxHeight: "100%"
                },
                children: showWelcome ? /* @__PURE__ */ jsx("div", { style: { flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }, children: conversationContent }) : /* @__PURE__ */ jsxs(Conversation, { className: "conversation-container", children: [
                  /* @__PURE__ */ jsx(ConversationContent, { className: "tw:flex tw:items-center tw:justify-center", children: conversationContent }),
                  !isMobile && /* @__PURE__ */ jsx(ConversationScrollButton, {})
                ] })
              }
            ),
            viewMode !== "read-only" && (isEmbedded || !showWelcome) && /* @__PURE__ */ jsx(
              ConciergeInputArea,
              {
                searchInputValue,
                setSearchInputValue,
                handleSearch,
                isActivelyStreaming,
                conversationHistoryLength: conversationHistory.length,
                onTrackAccessPoint,
                onStop,
                widgetId: isEmbedded ? "concierge-panel-conversation-chat-input" : "concierge-conversation-chat-input",
                displayName: isEmbedded ? "Chat Input (Panel Conversation)" : "Chat Input (Conversation)",
                presets: isEmbedded ? PANEL_CHAT_INPUT_PRESETS : void 0,
                forceMobile: isEmbedded,
                initialState: isEmbedded ? { preset: "compact", ...PANEL_CHAT_INPUT_PRESETS.compact } : { preset: "compact", ...CHAT_INPUT_PRESETS.compact },
                liveTakeoverActive: effectiveTakeoverActive,
                adminName: takeover.adminName,
                adminAvatarUrl: takeover.adminAvatarUrl,
                onEndLiveAgent: handleEndLiveAgent
              }
            )
          ] })
        ]
      }
    ) });
  };
  if (isEmbedded) {
    return /* @__PURE__ */ jsx(
      ConciergePanelWidget,
      {
        widgetId: "concierge-panel",
        displayName: "Concierge Panel",
        suggestionListWidgetId: "concierge-suggestion-list",
        chatInputWidgetId: "concierge-panel-chat-input",
        suppressChildEdits: false,
        children: (panelState, resolveColor) => renderContent(panelState, resolveColor)
      }
    );
  }
  return renderContent(directPanelState, directResolveColor);
};
const ConciergeChatAreaV2 = memo(ConciergeChatAreaV2Component);
export {
  ConciergeChatAreaV2
};
//# sourceMappingURL=index.js.map
